import { useState } from 'react'
import { ScrollView, Text, View, useWindowDimensions } from 'react-native'
import { useTranslation } from 'react-i18next'
import { screens } from '@chillout/core'
import { Button, Stepper, TextField } from '@chillout/ui'
import type { CashierMemberProfile } from '../home/useMemberSearch'
import { MemberInfoHeader } from '../stampAwarding/MemberInfoHeader'
import { useMemberStampData } from '../stampAwarding/useMemberStampData'
import { useStampSettings } from '../stampAwarding/useStampSettings'
import { StampPreview } from '../stampAwarding/StampPreview'
import { SelectField } from '../../shared/SelectField'

interface StampCorrectionSheetProps {
  member: CashierMemberProfile
  onClose: () => void
  onRequestPin: (removalCount: number, reason: string, note: string) => void
}

export function StampCorrectionSheet({ member, onClose, onRequestPin }: StampCorrectionSheetProps) {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const { data: stampData } = useMemberStampData(member.id)
  const { data: settings } = useStampSettings()
  const [removalCount, setRemovalCount] = useState(0)
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')

  const reasonOptions = (
    ['customer_complaint', 'system_error', 'cashier_mistake', 'promotion', 'other'] as const
  ).map((key) => ({ value: key, label: t(`common.adjustmentReasons.${key}`) }))

  const currentStamps = stampData?.currentStamps ?? 0
  const twoColumn = width >= screens.lg

  const countLabel = removalCount === 0
    ? t('stampCorrection.stepper.stampZero')
    : removalCount === -1
      ? t('stampCorrection.stepper.stampSingle')
      : t('stampCorrection.stepper.stamps', { count: Math.abs(removalCount) })

  const stepper = (
    <Stepper
      value={removalCount}
      onChange={setRemovalCount}
      min={-currentStamps}
      max={0}
      label={t('stampCorrection.stepper.label')}
      countLabel={countLabel}
      decrementAccessibilityLabel={t('stampCorrection.stepper.decrementAccessibilityLabel')}
      incrementAccessibilityLabel={t('stampCorrection.stepper.incrementAccessibilityLabel')}
      countAccessibilityLabel={t('stampCorrection.stepper.countAccessibilityLabel', { count: removalCount })}
    />
  )

  const preview = settings ? (
    <StampPreview
      currentStamps={currentStamps}
      stampDelta={removalCount}
      stampTarget={settings.stampTarget}
      completionCardIcon={settings.completionCardIcon}
      milestones={settings.milestones}
    />
  ) : null

  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingBottom: 40 }}
    >
      <MemberInfoHeader
        member={member}
        title={t('stampCorrection.title')}
      />

      <View className="px-6 gap-6">
        {twoColumn ? (
          <View style={{ flexDirection: 'row', gap: 32 }}>
            <View style={{ flex: 1, gap: 16 }}>
              {preview}
              <Text className="text-base text-secondary">
                {t('stampCorrection.disclaimer')}
              </Text>
            </View>
            <View style={{ flex: 1, gap: 16 }}>
              {stepper}
              <SelectField
                label={t('stampCorrection.reason.label')}
                value={reason}
                options={reasonOptions}
                placeholder={t('stampCorrection.reason.placeholder')}
                onValueChange={setReason}
              />
              <TextField
                label={t('stampCorrection.note')}
                placeholder={t('stampCorrection.notePlaceholder')}
                value={note}
                onChangeText={setNote}
                multiline
                maxLength={200}
              />
              <View className="gap-3">
                <Button
                  label={t('stampCorrection.removeButton')}
                  onPress={() => onRequestPin(removalCount, reason, note)}
                  disabled={removalCount === 0 || reason === ''}
                />
                <Button
                  label={t('common.cancel')}
                  onPress={onClose}
                  variant="ghost"
                />
              </View>
            </View>
          </View>
        ) : (
          <View className="gap-4">
            {stepper}
            {preview}
            <SelectField
              label={t('stampCorrection.reason.label')}
              value={reason}
              options={reasonOptions}
              placeholder={t('stampCorrection.reason.placeholder')}
              onValueChange={setReason}
            />
            <TextField
              label={t('stampCorrection.note')}
              placeholder={t('stampCorrection.notePlaceholder')}
              value={note}
              onChangeText={setNote}
              multiline
              maxLength={200}
            />
            <Text className="text-base text-secondary">
              {t('stampCorrection.disclaimer')}
            </Text>
            <View className="gap-3">
              <Button
                label={t('stampCorrection.removeButton')}
                onPress={() => onRequestPin(removalCount, reason, note)}
                disabled={removalCount === 0 || reason === ''}
              />
              <Button
                label={t('common.cancel')}
                onPress={onClose}
                variant="ghost"
              />
            </View>
          </View>
        )}
      </View>
    </ScrollView>
  )
}